import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useApp } from 'contexts/app';
import PricingService from 'services/pricing';
import ProductVariantService from 'services/product_variant';
import ProductTagService from 'services/product_tag';
import ProductCategoryService from 'services/product_category';
import _ from 'lodash';

const pricingService = new PricingService();
const variantService = new ProductVariantService();
const tagService = new ProductTagService();
const categoryService = new ProductCategoryService();

const applyAutoRule = (price, autoRule) => {
    if (!autoRule || !price) return price;
    if (autoRule.type === 'percent') {
        return Math.round(price * (1 - (autoRule.value || 0) / 100));
    }
    if (autoRule.type === 'fixed_amount') {
        return Math.max(0, price - (autoRule.value || 0));
    }
    return price;
};

const isVariantInScope = (variant, applyTo, scopeIds) => {
    if (applyTo === 'all') return true;
    if (applyTo === 'tag') {
        const productTags = variant?.product?.tags ?? [];
        return (scopeIds || []).some(tid => productTags.includes(tid));
    }
    if (applyTo === 'category') {
        const cat = variant?.product?.category_id ?? variant?.product?.product_category_id;
        return (scopeIds || []).includes(cat);
    }
    if (applyTo === 'manual') {
        return (scopeIds || []).includes(variant?.product_id);
    }
    return true;
};

const normalizeAutoRuleFromServer = (autoRule) => {
    if (!autoRule) return { type: 'percent', value: 0 };
    const type = autoRule.type || autoRule.discount_type || 'percent';
    const value = Number(autoRule.value ?? autoRule.discount_value ?? 0) || 0;
    return {
        type: type === 'fixed_amount' ? 'fixed_amount' : 'percent',
        value: Math.max(0, value),
    };
};

const toAutoRulePayload = (autoRule) => {
    const normalized = normalizeAutoRuleFromServer(autoRule);
    return {
        discount_type: normalized.type,
        discount_value: normalized.value,
    };
};

const useComponent = () => {
    const { showLoading, showNotify } = useApp();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;

    const [name, setName] = useState('');
    const [mode, setMode] = useState('manual');
    const [applyTo, setApplyTo] = useState('all');
    const [tagIds, setTagIds] = useState([]);
    const [categoryIds, setCategoryIds] = useState([]);
    const [productIds, setProductIds] = useState([]);
    const [autoRule, setAutoRule] = useState({ type: 'percent', value: 0 });

    const [allVariants, setAllVariants] = useState([]);
    const [priceItems, setPriceItems] = useState({}); // { variant_id: { original_price, price_list } }
    const [tags, setTags] = useState([]);
    const [categories, setCategories] = useState([]);
    const [customers, setCustomers] = useState([]);

    useEffect(() => {
        loadInitial();
    }, []);

    const loadInitial = async () => {
        showLoading(true);
        const [variants, tagList, catList] = await Promise.all([
            variantService.list({ info: '' }).catch(() => []),
            tagService.list().catch(() => []),
            categoryService.list().catch(() => []),
        ]);
        setAllVariants(variants ?? []);
        setTags((tagList ?? []).map(t => ({ label: t.name, value: t.id })));
        setCategories((catList ?? []).map(c => ({ label: c.name, value: c.id })));

        if (isEdit) {
            const [table, items, customerList] = await Promise.all([
                pricingService.getByID(id),
                pricingService.getPriceItems(id),
                pricingService.getCustomers(id).catch(() => []),
            ]);
            if (table) {
                setName(table.name ?? '');
                setMode(table.mode ?? 'manual');
                setApplyTo(table.apply_to ?? 'all');
                setTagIds(table.tag_ids ?? []);
                setCategoryIds(table.category_ids ?? []);
                setProductIds(table.product_ids ?? []);
                setAutoRule(normalizeAutoRuleFromServer(table.auto_rule));
            }
            if (items && items.length > 0) {
                const map = {};
                for (const item of items) {
                    map[item.variant_id] = { original_price: item.original_price, price_list: item.price_list ?? [] };
                }
                setPriceItems(map);
            }
            setCustomers(customerList ?? []);
        }
        showLoading(false);
    };

    const scopedVariants = useMemo(() => {
        const scopeIds = applyTo === 'tag' ? tagIds
            : applyTo === 'category' ? categoryIds
                : applyTo === 'manual' ? productIds
                    : [];
        return allVariants.filter(v => isVariantInScope(v, applyTo, scopeIds));
    }, [allVariants, applyTo, tagIds, categoryIds, productIds]);

    const onPriceItemChange = useCallback((variant_id, field, value) => {
        setPriceItems(prev => ({
            ...prev,
            [variant_id]: { ...(prev[variant_id] || {}), [field]: value },
        }));
    }, []);

    const onAddCustomer = useCallback(async (customer) => {
        if (!customer?.id || !id) return;
        if (customers.some(c => String(c.id) === String(customer.id))) return;
        try {
            await pricingService.setCustomerPriceTable({ customer_id: customer.id, price_table_id: id });
            setCustomers(prev => {
                if (prev.some(c => String(c.id) === String(customer.id))) return prev;
                return [...prev, customer];
            });
            showNotify('Đã thêm khách hàng vào bảng giá');
        } catch (e) {
            showNotify('Không thể thêm khách hàng. Vui lòng thử lại');
        }
    }, [id, customers, showNotify]);

    const onRemoveCustomer = useCallback(async (customer_id) => {
        if (!customer_id) return;
        try {
            await pricingService.setCustomerPriceTable({ customer_id, price_table_id: null });
            setCustomers(prev => prev.filter(c => String(c.id) !== String(customer_id)));
            showNotify('Đã gỡ khách hàng khỏi bảng giá');
        } catch (e) {
            showNotify('Không thể gỡ khách hàng. Vui lòng thử lại');
        }
    }, [showNotify]);

    const onSave = async () => {
        if (!name.trim()) {
            showNotify('Vui lòng nhập tên bảng giá');
            return;
        }
        showLoading(true);
        const tableData = {
            id: isEdit ? id : undefined,
            name,
            mode,
            apply_to: applyTo,
            tag_ids: applyTo === 'tag' ? tagIds : [],
            category_ids: applyTo === 'category' ? categoryIds : [],
            product_ids: applyTo === 'manual' ? productIds : [],
            auto_rule: mode === 'auto' ? toAutoRulePayload(autoRule) : null,
        };

        let savedId = id;
        if (isEdit) {
            await pricingService.update(tableData);
        } else {
            const created = await pricingService.create(tableData);
            savedId = created?.id;
        }

        if (mode === 'manual' && savedId) {
            const items = Object.entries(priceItems).map(([variant_id, v]) => ({
                variant_id,
                original_price: v.original_price ?? 0,
                price_list: v.price_list ?? [],
            }));
            await pricingService.savePriceItems({ id: savedId, items });
        }

        showLoading(false);
        showNotify('Đã lưu bảng giá');
        navigate('/pricing');
    };

    return {
        tableId: id,
        isEdit, name, setName,
        mode, setMode,
        applyTo, setApplyTo,
        tagIds, setTagIds,
        categoryIds, setCategoryIds,
        productIds, setProductIds,
        autoRule, setAutoRule,
        scopedVariants, priceItems, onPriceItemChange,
        tags, categories,
        applyAutoRule,
        customers, onAddCustomer, onRemoveCustomer,
        onSave, navigate,
    };
};

export default useComponent;
